/**
 * Build a single force-graph combining threat + infrastructure + business layers.
 * Ported from arbitiumtriage Network (layer All) into one merged graph with cross-layer edges.
 */

import type {
  BusinessElementDocument,
  InfrastructureElementDocument,
  ThreatElementDocument,
} from '../../../api-utils/elements';
import { isCrownJewel } from '../../../lib/crownJewel';

export type ElementGraphLayer = 'infrastructure' | 'business' | 'threat';
export type ThreatSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface ForceGraphNode {
  id: string;
  label: string;
  group: string;
  universalType: string;
  universalDomain: string;
  provider: string;
  providerType: string;
  externalId: string;
  name: string;
  linkCount: number;
  isDangling?: boolean;
  layer?: ElementGraphLayer;
  needsDecision?: boolean;
  /** Threat diamond size key (critical → low). */
  severity?: ThreatSeverity;
  /** 1-based attack order in incident scope; omitted when a single action is shown. */
  attackStep?: number;
  /** Campaign id for hull clustering (campaign / entity / everything). */
  campaignCluster?: string;
  /** EBR map: blast-radius walk origin (hexagon, larger). */
  isBlastOrigin?: boolean;
  /** The asset carries the Crown Jewels tag; set only when it does. */
  crownJewel?: boolean;
  /** The words the owner of the asset put on it (Crown Jewel, Position); set only when there are some. */
  tags?: string[];
}

export interface ForceGraphLink {
  source: string;
  target: string;
  label: string;
  dangling?: boolean;
  crossLayer?: boolean;
}

export interface ForceGraphData {
  nodes: ForceGraphNode[];
  links: ForceGraphLink[];
  stats: {
    elementCount: number;
    nodeCount: number;
    linkCount: number;
    crossLayerLinkCount: number;
  };
}

interface ElementLinkRef {
  label?: string[];
  value?: string;
  attributes?: Record<string, unknown>;
  extras?: Record<string, unknown>;
  qualifiers?: Record<string, unknown>;
}

const EMPTY_GRAPH: ForceGraphData = {
  nodes: [],
  links: [],
  stats: { elementCount: 0, nodeCount: 0, linkCount: 0, crossLayerLinkCount: 0 },
};

function normalize(value: unknown, fallback = ''): string {
  const text = String(value ?? '').trim();
  return text || fallback;
}

function normalizeSeverity(value: unknown): ThreatSeverity {
  const severity = String(value ?? '').toLowerCase();
  return severity === 'critical' || severity === 'high' || severity === 'medium' || severity === 'low'
    ? severity
    : 'medium';
}

function linkLabel(link: ElementLinkRef): string {
  if (Array.isArray(link.label) && link.label.length > 0) {
    return link.label.filter(Boolean).join(' / ');
  }
  return 'RELATES_TO';
}

function linkBags(link: ElementLinkRef): Record<string, unknown> {
  const attributes =
    link.attributes && typeof link.attributes === 'object' ? link.attributes : {};
  const extras = link.extras && typeof link.extras === 'object' ? link.extras : {};
  if (
    !Object.keys(attributes).length &&
    !Object.keys(extras).length &&
    link.qualifiers &&
    typeof link.qualifiers === 'object'
  ) {
    return link.qualifiers;
  }
  return { ...extras, ...attributes };
}

function isDerivedRelationship(bag: Record<string, unknown>): boolean {
  return bag.derived === true || bag.relationship_origin === 'derived';
}

function toInfraLikeBusiness(items: BusinessElementDocument[]): InfrastructureElementDocument[] {
  return items.map((item) => {
    const id = normalize(item._id);
    const name = normalize(item.name, id || 'unknown');
    const elementType = normalize(item.element_type, 'unknown');
    return {
      _id: id,
      name,
      provider: 'business',
      provider_type: elementType,
      universal_type: elementType,
      universal_domain: elementType,
      external_id: name,
      tags: item.tags,
      links: Array.isArray(item.links) ? item.links : [],
    };
  });
}

function toInfraLikeThreat(items: ThreatElementDocument[]): InfrastructureElementDocument[] {
  return items.map((item) => {
    const id = normalize(item._id);
    const name = normalize(item.name, id || 'unknown');
    const elementType = normalize(item.element_type, 'unknown');
    const status = normalize(item.status).toLowerCase();
    return {
      _id: id,
      name,
      provider: 'threat',
      provider_type: elementType,
      universal_type: elementType,
      universal_domain: elementType,
      external_id: name,
      links: Array.isArray(item.links) ? item.links : [],
      needsDecision: status === 'candidate' || status === 'open',
      severity: normalizeSeverity(item.detection?.detection_risk_level),
    } as InfrastructureElementDocument & {
      needsDecision?: boolean;
      severity?: ThreatSeverity;
    };
  });
}

function nodeFromDoc(
  doc: InfrastructureElementDocument,
  layer: ElementGraphLayer
): ForceGraphNode | null {
  const id = normalize(doc._id);
  if (!id) return null;
  const universalDomain = normalize(doc.universal_domain, 'other');
  const name = normalize(doc.name);
  const externalId = normalize(doc.external_id);
  const meta = doc as { needsDecision?: boolean; severity?: ThreatSeverity };
  return {
    id,
    label: name || externalId || id,
    group: universalDomain,
    universalType: normalize(doc.universal_type, 'unknown'),
    universalDomain,
    provider: normalize(doc.provider, 'unknown'),
    providerType: normalize(doc.provider_type, 'unknown'),
    externalId,
    name,
    linkCount: 0,
    layer,
    needsDecision: Boolean(meta.needsDecision),
    severity: layer === 'threat' ? normalizeSeverity(meta.severity) : undefined,
    ...(isCrownJewel(doc) ? { crownJewel: true } : {}),
    ...(Array.isArray(doc.tags) && doc.tags.length > 0 ? { tags: doc.tags.map(String) } : {}),
  };
}

function buildLayerGraph(
  items: InfrastructureElementDocument[],
  layer: ElementGraphLayer
): { nodes: ForceGraphNode[]; links: ForceGraphLink[] } {
  const nodesById = new Map<string, ForceGraphNode>();
  const links: ForceGraphLink[] = [];
  const linkKeys = new Set<string>();

  for (const item of items) {
    const node = nodeFromDoc(item, layer);
    if (node) nodesById.set(node.id, node);
  }

  for (const item of items) {
    const sourceId = normalize(item._id);
    if (!sourceId || !nodesById.has(sourceId)) continue;
    const rawLinks = item.links;
    if (!Array.isArray(rawLinks)) continue;

    for (const raw of rawLinks) {
      if (!raw || typeof raw !== 'object') continue;
      const link = raw as ElementLinkRef;
      const bag = linkBags(link);
      if (isDerivedRelationship(bag)) continue;
      const targetId = normalize(link.value);
      if (!targetId || targetId.startsWith('_dangling/')) continue;
      if (!nodesById.has(targetId)) continue;

      const label = linkLabel(link);
      const key = `${sourceId}\u0000${targetId}\u0000${label}\u0000intra`;
      if (linkKeys.has(key)) continue;
      linkKeys.add(key);
      links.push({ source: sourceId, target: targetId, label });
    }
  }

  // Keep only connected nodes for a cleaner Investigation Workspace view.
  const connected = new Set<string>();
  for (const link of links) {
    connected.add(link.source);
    connected.add(link.target);
  }
  const nodes = Array.from(nodesById.values()).filter((n) => connected.has(n.id));
  return { nodes, links };
}

function refEntries(
  doc: Record<string, unknown>,
  field: string
): Array<{ targetId: string; label: string }> {
  const refs = doc[field];
  if (!Array.isArray(refs)) return [];
  const out: Array<{ targetId: string; label: string }> = [];
  for (const raw of refs) {
    if (!raw || typeof raw !== 'object') continue;
    const link = raw as ElementLinkRef;
    const targetId = normalize(link.value);
    if (!targetId || targetId.startsWith('_dangling/')) continue;
    out.push({ targetId, label: linkLabel(link) });
  }
  return out;
}

/**
 * Merge infrastructure, business, and threat element graphs into one view,
 * including cross-layer edges from infrastructure_refs / business_refs.
 */
export function buildCombinedThreeLayerGraph(
  infrastructure: InfrastructureElementDocument[],
  business: BusinessElementDocument[],
  threat: ThreatElementDocument[]
): ForceGraphData {
  if (infrastructure.length === 0 && business.length === 0 && threat.length === 0) {
    return EMPTY_GRAPH;
  }

  const infraGraph = buildLayerGraph(infrastructure, 'infrastructure');
  const businessGraph = buildLayerGraph(toInfraLikeBusiness(business), 'business');
  const threatGraph = buildLayerGraph(toInfraLikeThreat(threat), 'threat');

  const nodesById = new Map<string, ForceGraphNode>();
  for (const node of [...infraGraph.nodes, ...businessGraph.nodes, ...threatGraph.nodes]) {
    if (!nodesById.has(node.id)) {
      nodesById.set(node.id, { ...node, linkCount: 0 });
    }
  }

  const links: ForceGraphLink[] = [];
  const linkKeys = new Set<string>();

  const pushLink = (link: ForceGraphLink) => {
    const key = `${link.source}\u0000${link.target}\u0000${link.label}\u0000${
      link.crossLayer ? 'cross' : 'intra'
    }`;
    if (linkKeys.has(key)) return;
    if (!nodesById.has(link.source) || !nodesById.has(link.target)) return;
    linkKeys.add(key);
    links.push(link);
  };

  for (const link of [...infraGraph.links, ...businessGraph.links, ...threatGraph.links]) {
    pushLink(link);
  }

  // Ensure all layer nodes exist when adding cross-layer edges (even if unconnected intra-layer).
  const ensureFromPool = (
    id: string,
    layer: ElementGraphLayer,
    pool: InfrastructureElementDocument[]
  ) => {
    if (nodesById.has(id)) return true;
    const doc = pool.find((item) => normalize(item._id) === id);
    if (!doc) return false;
    const node = nodeFromDoc(doc, layer);
    if (!node) return false;
    nodesById.set(id, node);
    return true;
  };

  const infraPool = infrastructure;
  const businessPool = toInfraLikeBusiness(business);
  const threatPool = toInfraLikeThreat(threat);

  for (const doc of business) {
    const sourceId = normalize(doc._id);
    if (!sourceId) continue;
    ensureFromPool(sourceId, 'business', businessPool);
    for (const ref of refEntries(doc as Record<string, unknown>, 'infrastructure_refs')) {
      if (ensureFromPool(ref.targetId, 'infrastructure', infraPool)) {
        pushLink({
          source: sourceId,
          target: ref.targetId,
          label: ref.label,
          crossLayer: true,
        });
      }
    }
  }

  for (const doc of threat) {
    const sourceId = normalize(doc._id);
    if (!sourceId) continue;
    ensureFromPool(sourceId, 'threat', threatPool);
    for (const ref of refEntries(doc as Record<string, unknown>, 'infrastructure_refs')) {
      if (ensureFromPool(ref.targetId, 'infrastructure', infraPool)) {
        pushLink({
          source: sourceId,
          target: ref.targetId,
          label: ref.label,
          crossLayer: true,
        });
      }
    }
    for (const ref of refEntries(doc as Record<string, unknown>, 'business_refs')) {
      if (ensureFromPool(ref.targetId, 'business', businessPool)) {
        pushLink({
          source: sourceId,
          target: ref.targetId,
          label: ref.label,
          crossLayer: true,
        });
      }
    }
  }

  for (const link of links) {
    const source = nodesById.get(link.source);
    const target = nodesById.get(link.target);
    if (source) source.linkCount += 1;
    if (target) target.linkCount += 1;
  }

  const connected = new Set<string>();
  for (const link of links) {
    connected.add(link.source);
    connected.add(link.target);
  }
  const nodes = Array.from(nodesById.values()).filter((n) => connected.has(n.id));
  const crossLayerLinkCount = links.filter((l) => l.crossLayer).length;

  return {
    nodes,
    links,
    stats: {
      elementCount: infrastructure.length + business.length + threat.length,
      nodeCount: nodes.length,
      linkCount: links.length,
      crossLayerLinkCount,
    },
  };
}
